import { CSSProperties, ReactNode, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as Copy } from "../../../assets/svg/copy.svg";
import { SPACING } from "../../../constants/design/spacing";
import { copyStringToClipboard, shortenString } from "../../../utils/strings";
import { BaseModal } from "../../BaseModal";
import MarketCell from "../../PortfolioSettings/Table/MarketCell";
import {
  ViewDetailsContent,
  ViewDetailsRow,
} from "../../shared/ViewDetailsModal/style";

interface IHistoryDetailsShellProps {
  show: boolean;
  onHide?: () => void;
  /** Market name shown in the modal header. */
  instrumentName?: string;
  /**
   * Real leverage for the row, shown next to the market name. Omit (or pass a
   * non-positive value) when the row has no leverage — the header then hides
   * it rather than showing a manufactured `1x`.
   */
  leverage?: number;
  /** Body rows — pre-composed by the per-endpoint modal. */
  children: ReactNode;
  /** ID row at the bottom of the modal (copy-to-clipboard). */
  copyId?: { label: string; value?: string };
  style?: CSSProperties;
}

/**
 * Shared chrome for the three history detail modals.
 *
 * Renders BaseModal + market-name title + body rows + a copy-to-clipboard
 * ID footer. The per-endpoint modals are responsible for the row content.
 */
function HistoryDetailsShell({
  show,
  onHide,
  instrumentName,
  leverage,
  children,
  copyId,
  style,
}: IHistoryDetailsShellProps) {
  const { t } = useTranslation("app", {
    keyPrefix: "components.History.details.HistoryDetailsShell",
  });
  const [showCopied, setShowCopied] = useState(false);

  useEffect(() => {
    if (!showCopied) return;
    const timer = window.setTimeout(() => setShowCopied(false), 800);
    return () => window.clearTimeout(timer);
  }, [showCopied]);

  const onCopy = useCallback(() => {
    if (!copyId?.value) return;
    copyStringToClipboard(copyId.value);
    setShowCopied(true);
  }, [copyId]);

  return (
    <BaseModal
      title={instrumentName ? <MarketCell instrumentName={instrumentName} leverage={leverage ?? 0} /> : undefined}
      show={show}
      onHide={onHide}
      style={{ minWidth: 311, ...style }}
    >
      <ViewDetailsContent>
        {children}
        {copyId?.value && (
          <ViewDetailsRow clickable onClick={onCopy}>
            <span>{copyId.label}</span>
            <span>
              <Copy style={{ marginRight: SPACING.two }} />
              {showCopied
                ? t("copied", { defaultValue: "Copied" })
                : shortenString(copyId.value, 5, 5)}
            </span>
          </ViewDetailsRow>
        )}
      </ViewDetailsContent>
    </BaseModal>
  );
}

export default HistoryDetailsShell;
